import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { authorPostsRoute } from "app/router";
import { Avatar, Card, Spinner, TablePagination } from "components/elements";
import { BlogPreview } from "components/elements/BlogPreview";
import { FavoriteArticlesSidebar } from "components/layout/FavoriteArticlesSidebar";
import { RowMasonry } from "components/layout/RowMasonry";
import { TrendingSidebar } from "components/layout/TrendingSidebar";
import { trpc } from "lib/trpc";
import { ResourcesPostPreview } from "@compass/shared/types";

export const AuthorPosts = () => {
    const { id } = useParams({ from: authorPostsRoute.id });
    const authorId = Number(id);

    const [pageSize, setPageSize] = useState<number | undefined>(10);
    const [currentPage, setCurrentPage] = useState<number | undefined>(0);

    const [author] = trpc.resources.getAuthor.useSuspenseQuery({ id: authorId });
    const postsQuery = trpc.resources.latestPosts.useQuery({
        authorId,
        page: currentPage ? currentPage + 1 : 1,
        limit: pageSize || 10,
    });

    if ("error" in author) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <h1 className="text-4xl font-bold text-slate-500">404</h1>
                <p className="flex text-2xl">Author not found</p>
                <Link to={`/c/blog` as "/social"} className="text-sm underline">
                    Back
                </Link>
            </div>
        );
    }

    const posts = postsQuery.data;

    if (posts && "error" in posts) {
        throw new Error();
    }

    const category = posts?.posts?.[0]?.categories?.[0] ?? "blog";

    return (
        <Card>
            <div className="mb-4 flex items-start gap-4">
                <Avatar
                    className="h-20 w-20"
                    photoUrl={author.avatarUrl}
                    name={author.name}
                    seed={author.id ?? author.name}
                />
                <div className="flex-1">
                    <h1 className="font-sans text-[28px] font-semibold text-[#424242]">
                        {author.name}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {author.postCount} {author.postCount === 1 ? "post" : "posts"}
                    </p>
                    {author.bio && <p className="mt-2 text-sm text-[#424242]">{author.bio}</p>}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    {postsQuery.isPending && (
                        <div className="flex h-60 items-center justify-center">
                            <Spinner className="text-slate-500" />
                        </div>
                    )}

                    {!postsQuery.isPending && !posts?.posts?.length && (
                        <p className="text-center">This author hasn't published any posts yet.</p>
                    )}

                    {Boolean(posts?.posts?.length) && (
                        <RowMasonry
                            items={posts?.posts ?? []}
                            getKey={(post) => post.id}
                            columns={{ base: 1, md: 2, lg: 3 }}
                            renderItem={(post: ResourcesPostPreview) => (
                                <BlogPreview post={post} category={category} />
                            )}
                        />
                    )}

                    {(posts?.posts?.length ?? 0) > 0 && (
                        <div className="mt-6">
                            <TablePagination
                                total={posts?.totalCount || 1}
                                page={currentPage}
                                onChangePage={setCurrentPage}
                                pageSize={pageSize}
                                onChangePageSize={setPageSize}
                                message="Items per page"
                            />
                        </div>
                    )}
                </div>

                <aside className="hidden lg:block">
                    <TrendingSidebar filterType="blogPost" title="Trending" limit={20} />
                    <div className="mt-4" />
                    <FavoriteArticlesSidebar />
                </aside>
            </div>
        </Card>
    );
};
